import { doc, setDoc } from 'firebase/firestore';
import { useState } from 'react';
import { auth, db } from '../firebase';

const CONSENT_VERSION = '1.0';

const styles = {
  screen: {
    minHeight: '100vh',
    backgroundColor: '#0A2D5A',
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '40px',
    width: '100%',
    maxWidth: 560,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  },
  header: { textAlign: 'center', marginBottom: 28 },
  shield: { width: 72, height: 72, margin: '0 auto 16px', display: 'block' },
  brandName: {
    fontSize: 13, fontWeight: '700', color: '#E8620A',
    letterSpacing: 2, marginBottom: 8,
  },
  title: { fontSize: 24, fontWeight: '700', color: '#0A2D5A', marginBottom: 8 },
  sub: { fontSize: 15, color: '#666', lineHeight: 1.5 },
  divider: { height: 1, backgroundColor: '#eee', margin: '24px 0' },
  agreementBox: {
    backgroundColor: '#F4F6F9', borderRadius: 10, padding: '20px 22px',
    marginBottom: 24, borderLeft: '4px solid #1D6FA4',
  },
  agreementText: { fontSize: 14, color: '#333', lineHeight: 1.7 },
  agentNameHighlight: { fontWeight: '700', color: '#0A2D5A' },
  link: { color: '#1D6FA4', textDecoration: 'underline' },
  checkboxRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    marginBottom: 24, cursor: 'pointer',
  },
  checkbox: {
    width: 20, height: 20, marginTop: 2, flexShrink: 0,
    cursor: 'pointer', accentColor: '#E8620A',
  },
  checkboxLabel: { fontSize: 14, color: '#333', lineHeight: 1.5 },
  button: {
    width: '100%', padding: '14px', color: 'white', border: 'none',
    borderRadius: 8, fontSize: 16, fontWeight: '600',
  },
  errorText: { fontSize: 13, color: '#C0392B', marginTop: 12, textAlign: 'center' },
};

export default function Consent({ agentName, onComplete }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const displayName = agentName || 'the account holder';

  const handleAgree = async () => {
    if (!checked || saving) return;
    setSaving(true);
    setError('');
    try {
      const agentId = auth.currentUser?.uid;
      if (agentId) {
        await setDoc(
          doc(db, 'agents', agentId),
          {
            consentAccepted: true,
            consentTimestamp: new Date().toISOString(),
            consentVersion: CONSENT_VERSION,
            consentName: agentName || '',
          },
          { merge: true }
        );
      }
      onComplete();
    } catch (err) {
      console.error('Error saving consent:', err);
      setError('Something went wrong saving your agreement. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={styles.screen}>
      <div style={styles.card}>

        <div style={styles.header}>
          <img src="/icon.png" alt="Medicare Plan Guardian" style={styles.shield} />
          <div style={styles.brandName}>MEDICARE PLAN GUARDIAN</div>
          <div style={styles.title}>Before You Begin</div>
          <div style={styles.sub}>
            Please review and accept the agreement below to activate your account.
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.agreementBox}>
          <div style={styles.agreementText}>
            By activating your Medicare Plan Guardian account, I,{' '}
            <span style={styles.agentNameHighlight}>{displayName}</span>, agree that it is
            my sole responsibility to obtain proper consent from each client before adding
            them to the Medicare Plan Guardian system. I agree that clients I add have
            consented to receive Medicare plan reminders from my agency. I agree to the
            Medicare Plan Guardian{' '}
            <a href="https://medicareplanguardian.com/terms-of-service/" target="_blank" rel="noopener noreferrer" style={styles.link}>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://medicareplanguardian.com/privacy-policy/" target="_blank" rel="noopener noreferrer" style={styles.link}>
              Privacy Policy
            </a>
            .
          </div>
        </div>

        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            style={styles.checkbox}
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span style={styles.checkboxLabel}>
            I have read and agree to the statement above.
          </span>
        </label>

        <button
          style={{
            ...styles.button,
            backgroundColor: checked && !saving ? '#E8620A' : '#C7CBD1',
            cursor: checked && !saving ? 'pointer' : 'not-allowed',
          }}
          onClick={handleAgree}
          disabled={!checked || saving}
        >
          {saving ? 'Saving...' : 'I Agree & Continue'}
        </button>

        {error ? <div style={styles.errorText}>{error}</div> : null}

      </div>
    </div>
  );
}
